import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType } from 'api/models';
import { getConnectorArticleId } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Group } from 'three';

const MARKER_RENDER_ORDER = 10000;

export const ConnectorSnapPointsMarker = () => {
    const markerGroupRef = useRef<Group | null>(null);

    const { candidates } = usePlacementStore(
        useShallow((state) => ({
            candidates: state.connectorSnapData.candidates
        }))
    );

    useFrame(() => {
        const group = markerGroupRef.current;
        if (!group) {
            return;
        }

        const state = usePlacementStore.getState();
        const isDragActive =
            state.isDragNewArticleStartRef.current ||
            state.isDragNewArticleActiveRef.current ||
            state.isAutoDragActiveRef.current ||
            state.isDragActiveRef.current;

        if (!isDragActive) {
            group.visible = false;
            return;
        }

        // Show markers if only connector articles are being dragged
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const dragAnchorId = selectedInstanceIds[0];
        const draggedArticleId = getConnectorArticleId(state, dragAnchorId);

        const baseVisible =
            !state.connectorSnapData.isLoading &&
            draggedArticleId !== null &&
            state.connectorSnapData.articleId === draggedArticleId &&
            state.connectorSnapData.candidates.length > 0 &&
            state.connectorSnapData.settingType === SettingModelSettingType.Connectors;

        group.visible = baseVisible;
    });

    const points = useMemo(
        () =>
            candidates.map((candidate, index) => (
                <mesh
                    key={`${candidate.position.x}-${candidate.position.y}-${candidate.position.z}-${index}`}
                    position={[candidate.position.x, candidate.position.y, candidate.position.z]}
                    renderOrder={MARKER_RENDER_ORDER}
                >
                    <sphereGeometry args={[0.03, 10, 10]} />
                    <meshBasicMaterial color={'#ff8a00'} depthTest={false} depthWrite={false} transparent />
                </mesh>
            )),
        [candidates]
    );

    return <group ref={markerGroupRef}>{points}</group>;
};
